/**
 * Klubovňa — domovská obrazovka Unmatched.
 *
 * Poradie na obrazovke je poradie otázok, ktoré si hráč kladie:
 *   1. Čaká niečo na mňa? → „Na ťahu si ty" úplne hore, s tlačidlom Hrať.
 *   2. Chcem začať novú hru → tri dlaždice podľa toho, s kým hrám
 *      (bot / kamarát online / jedna obrazovka) — vedú rovno do výberu
 *      s nastaveným režimom.
 *   3. Na čo čakám ja → stoly čakajúce na súpera (s tlačidlom na skopírovanie
 *      pozvánky) a rozohrané zápasy, kde ťahá súper alebo bot.
 * Bilancia a posledné výsledky sú bokom — sú to informácie, nie úlohy.
 *
 * Nad tým je banner: dve hotové postavy po stranách (pri každom načítaní iná
 * dvojica), v strede hlavná výzva „Založiť stôl" a pod ňou pozvánka zahrať
 * si proti botovi.
 *
 * Pod novou hrou sú verejné stoly cudzích hráčov (zakladateľ ich tak označil),
 * ku ktorým sa smie pridať ktokoľvek. Ostatné stoly sú súkromné — len s odkazom.
 * Časti mocku, ktoré by potrebovali nové služby (kto je online, chat), tu nie sú.
 */

import { content, portraitCard } from '@unmatched/content';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useEffect, useMemo, useState } from 'react';

import type { NewMode, Route } from '../../../App.js';
import { ApiError, BOT_LEVEL_LABEL, matches, type MatchMode, type MatchSummary, type PublicTable } from '../api.js';
import { cardImageUrl } from '../cardImage.js';

const MODE_NAME: Record<MatchMode, string> = {
  online: 'online',
  hotseat: 'hot-seat',
  solo: 'proti botovi',
};

interface Props {
  nick: string;
  onOpenMatch: (id: string) => void;
  onNavigate: (route: Route) => void;
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function heroName(id: string | null | undefined): string {
  if (!id) return '—';
  return content.heroes[id]?.name ?? id;
}

export function artOf(heroId: string | null | undefined): string | undefined {
  const card = heroId ? portraitCard(heroId) : undefined;
  return card ? cardImageUrl(card) : undefined;
}

export function mapName(id: string): string {
  return content.maps[id]?.name ?? id;
}

export function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'práve teraz';
  if (minutes < 60) return `pred ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `pred ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'včera' : `pred ${days} dňami`;
}

/** Sloty, za ktoré som v zápase sedel ja (v hot-seate všetky). */
function mySlots(match: MatchSummary, nick: string): number[] {
  return match.nicks.flatMap((n, slot) => (n === nick ? [slot] : []));
}

/** Víťaz je TÍM — v duele a FFA sa zhoduje so slotom, v 2v2 je to slot % 2. */
function slotWon(match: MatchSummary, slot: number): boolean {
  if (match.winner === null) return false;
  return match.format === 'teams' ? slot % 2 === match.winner : slot === match.winner;
}

export function inviteUrl(id: string): string {
  return `${window.location.origin}/#/unmatched/g/${id}`;
}

/** Tri spôsoby, ako začať — podľa toho, s kým hráč hrá. */
const STARTS: { mode: NewMode; icon: string; title: string; text: string }[] = [
  { mode: 'bot', icon: '⚙', title: 'Proti botovi', text: 'Začneš hneď. Tri obtiažnosti, na učenie aj na výzvu.' },
  { mode: 'online', icon: '⇄', title: 'Online', text: 'Pošleš odkaz kamarátovi, alebo založíš verejný stôl pre kohokoľvek.' },
  { mode: 'hotseat', icon: '▣', title: 'Na jednom počítači', text: 'Hot-seat: striedate sa pri jednej obrazovke.' },
];

const BANNER_POOL = IMPLEMENTED_HEROES.filter((id) => artOf(id));

/** Dve rôzne hotové postavy s artom — náhodne, nech banner nie je stále rovnaký. */
function pickDuo(): [string, string] {
  const first = Math.floor(Math.random() * BANNER_POOL.length);
  const second = (first + 1 + Math.floor(Math.random() * (BANNER_POOL.length - 1))) % BANNER_POOL.length;
  return [BANNER_POOL[first] ?? 'bigfoot', BANNER_POOL[second] ?? 'sinbad'];
}

/**
 * Art kariet nemá jednotné rozlíšenie (napr. 99×140) — na veľkom banneri by
 * bol rozmazaný. Postava s malým obrázkom sa po načítaní vymení za inú.
 */
const SHARP_ENOUGH = 200;

export function Home({ nick, onOpenMatch, onNavigate }: Props) {
  const [duo, setDuo] = useState(pickDuo);
  const [blurry, setBlurry] = useState<string[]>([]);

  function checkArt(index: number, img: HTMLImageElement) {
    if (img.naturalWidth >= SHARP_ENOUGH) return;
    const skip = [...blurry, ...duo];
    const next = BANNER_POOL.find((id) => !skip.includes(id));
    setBlurry((list) => [...list, duo[index]!]);
    if (next) setDuo((pair) => (index === 0 ? [next, pair[1]] : [pair[0], next]));
  }
  const [list, setList] = useState<MatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joinText, setJoinText] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [publicTables, setPublicTables] = useState<PublicTable[]>([]);

  // Verejné stoly sa menia bez nás (niekto založí, niekto sa pridá) — obnovujú sa priebežne.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { tables } = await matches.publicTables();
        if (alive) setPublicTables(tables.filter((t) => !t.mine));
      } catch {
        // Zoznam verejných stolov je doplnok — chyba nesmie pokaziť klubovňu.
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 20000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setList((await matches.list()).matches);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Nepodarilo sa načítať stoly');
        setList([]);
      }
    })();
  }, []);

  const all = list ?? [];
  const myTurn = all.filter((m) => m.status === 'playing' && m.your_move);
  const waiting = all.filter((m) => m.status === 'lobby' || (m.status === 'playing' && !m.your_move));
  const ended = all.filter((m) => m.status === 'ended');

  /**
   * Bilancia sa ráta len z online zápasov — v hot-seate hráš za všetkých
   * a výhra nad botom nie je výhra nad človekom.
   */
  const record = useMemo(() => {
    let wins = 0;
    let losses = 0;
    for (const match of ended) {
      if (match.mode !== 'online') continue;
      const [slot] = mySlots(match, nick);
      if (slot === undefined) continue;
      if (slotWon(match, slot)) wins++;
      else losses++;
    }
    return { wins, losses };
  }, [ended, nick]);

  function join(event: React.FormEvent) {
    event.preventDefault();
    const id = UUID.exec(joinText)?.[0];
    if (!id) {
      setJoinError('Vlož celý odkaz na stôl, ktorý ti poslali.');
      return;
    }
    onOpenMatch(id.toLowerCase());
  }

  const games = record.wins + record.losses;

  return (
    <div className="club">
      <section className="banner">
        {duo.map((heroId, index) => (
          <span key={heroId} className={`banner__fighter banner__fighter--${index}`} aria-hidden="true">
            <img src={artOf(heroId)} alt="" draggable={false} onLoad={(event) => checkArt(index, event.currentTarget)} />
            <span className="banner__name">{heroName(heroId)}</span>
          </span>
        ))}
        <div className="banner__center">
          <span className="tag tag--primary">Súbojnícka klubovňa</span>
          <h1 className="banner__title">
            Vyzvi <em>súpera</em>
          </h1>
          <button
            type="button"
            className="btn btn--accent btn--xl banner__cta"
            onClick={() => onNavigate({ name: 'new' })}
          >
            ⊕ Založiť stôl
          </button>
          <p className="banner__note">
            Nemáš s kým hrať? Už môžeš hrať aj <b>proti botovi</b> — v troch obtiažnostiach.{' '}
            <button type="button" className="banner__link" onClick={() => onNavigate({ name: 'new', mode: 'bot' })}>
              Hrať proti botovi →
            </button>
          </p>
        </div>
      </section>

      <header className="club__head">
        <p className="club__lead">
          Tvoje stoly a nové zápasy. K súkromnému stolu sa pripojí len ten, kto má odkaz; verejný vidí každý.
        </p>
        <form className="joinBox club__join" onSubmit={join}>
          <input
            value={joinText}
            onChange={(event) => {
              setJoinText(event.target.value);
              setJoinError(null);
            }}
            placeholder="Máš pozvánku? Vlož odkaz…"
            aria-label="Odkaz na stôl"
            spellCheck={false}
          />
          <button type="submit" className="btn btn--sm btn--primary" disabled={!joinText.trim()}>
            Pripojiť sa
          </button>
        </form>
      </header>
      {joinError ? <p className="formError">{joinError}</p> : null}
      {error ? <p className="formError">{error}</p> : null}

      {myTurn.length > 0 ? (
        <section className="club__block club__block--turn">
          <h2 className="club__label">
            <span className="dot dot--primary" /> Na ťahu si ty
            <span className="club__count">{myTurn.length}</span>
          </h2>
          <div className="club__rows">
            {myTurn.map((match) => (
              <TableRow key={match.id} match={match} nick={nick} onOpen={() => onOpenMatch(match.id)} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="club__block">
        <h2 className="club__label">Nová hra</h2>
        <div className="club__starts">
          {STARTS.map((start) => (
            <button
              key={start.mode}
              type="button"
              className="startTile"
              onClick={() => onNavigate({ name: 'new', mode: start.mode })}
            >
              <span className="startTile__icon" aria-hidden="true">
                {start.icon}
              </span>
              <span className="startTile__title">{start.title}</span>
              <span className="startTile__text">{start.text}</span>
              <span className="startTile__go">Vybrať postavy →</span>
            </button>
          ))}
        </div>
      </section>

      <section className="club__block">
        <h2 className="club__label">
          Verejné stoly
          <span className="club__count">{publicTables.length}</span>
        </h2>
        {publicTables.length === 0 ? (
          <p className="club__empty">
            Žiadny verejný stôl nečaká na hráča. Založ vlastný — pri online stole zvoľ „Verejný".
          </p>
        ) : (
          <div className="club__rows">
            {publicTables.map((table) => (
              <PublicRow key={table.id} table={table} onOpen={() => onOpenMatch(table.id)} />
            ))}
          </div>
        )}
      </section>

      <div className="club__grid">
        <section className="club__block">
          <h2 className="club__label">
            Čaká sa na súpera
            <span className="club__count">{waiting.length}</span>
          </h2>
          {list === null ? (
            <p className="muted">Načítavam…</p>
          ) : waiting.length === 0 ? (
            <p className="club__empty">
              {myTurn.length > 0
                ? 'Nikto ti nedlží ťah.'
                : 'Žiadny rozohraný stôl. Začni novú hru vyššie — proti botovi môžeš hrať hneď.'}
            </p>
          ) : (
            <div className="club__rows">
              {waiting.map((match) => (
                <TableRow key={match.id} match={match} nick={nick} onOpen={() => onOpenMatch(match.id)} />
              ))}
            </div>
          )}
        </section>

        <aside className="club__side">
          <section className="panel">
            <header className="panel__head">
              <h2 className="panel__title">Bilancia</h2>
              <span className="panel__meta">online zápasy</span>
            </header>
            {games === 0 ? (
              <p className="muted">
                Zatiaľ žiadny dohraný online zápas. Hry proti botovi a hot-seat sa nerátajú.
              </p>
            ) : (
              <>
                <div className="record">
                  <span className="record__big">
                    {record.wins} V <i>/</i> {record.losses} P
                  </span>
                  <span className="record__rate">{Math.round((record.wins / games) * 100)} %</span>
                </div>
                <div className="bar">
                  <span className="bar__fill" style={{ width: `${(record.wins / games) * 100}%` }} />
                </div>
              </>
            )}
          </section>

          <section className="panel">
            <header className="panel__head">
              <h2 className="panel__title">Dohrané</h2>
              <button type="button" className="panel__link" onClick={() => onNavigate({ name: 'matches' })}>
                všetky zápasy →
              </button>
            </header>
            {ended.length === 0 ? (
              <p className="muted">Keď dohráš prvý zápas, objaví sa tu.</p>
            ) : (
              <div className="results">
                {ended.slice(0, 5).map((match) => (
                  <ResultRow key={match.id} match={match} onOpen={() => onOpenMatch(match.id)} />
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

/** Kto je na druhej strane stola — pre riadok „Proti: …". */
function opponents(match: MatchSummary, nick: string): string {
  if (match.mode === 'hotseat') return 'všetci pri jednej obrazovke';
  if (match.mode === 'solo') {
    const bots = match.bot_levels.flatMap((level) => (level ? [BOT_LEVEL_LABEL[level].toLowerCase()] : []));
    return `proti botovi (${[...new Set(bots)].join(', ')})`;
  }
  const others = match.nicks.filter((n): n is string => Boolean(n) && n !== nick);
  return others.length ? `proti: ${others.join(', ')}` : 'súper sa ešte nepripojil';
}

/** Stav stola v jednej vete + čo s ním urobí tlačidlo. */
function tableStatus(match: MatchSummary): { label: string; tone: 'turn' | 'wait' | 'lobby' } {
  if (match.status === 'lobby') {
    const taken = match.seats_total - match.seats_free;
    return { label: `Čaká na súpera · ${taken}/${match.seats_total}`, tone: 'lobby' };
  }
  if (match.your_move) return { label: 'Si na ťahu', tone: 'turn' };
  return { label: match.mode === 'solo' ? 'Ťahá bot' : 'Ťahá súper', tone: 'wait' };
}

function TableRow({ match, nick, onOpen }: { match: MatchSummary; nick: string; onOpen: () => void }) {
  const [copied, setCopied] = useState(false);
  const status = tableStatus(match);
  const lobby = match.status === 'lobby';

  async function copy() {
    try {
      await navigator.clipboard.writeText(inviteUrl(match.id));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Schránka nemusí byť dostupná (http, oprávnenia) — pozvánku ukáže predsieň stola.
      onOpen();
    }
  }

  return (
    <article className={`tableRow tableRow--${status.tone}`}>
      <button type="button" className="tableRow__main" onClick={onOpen}>
        <span className="tableRow__duo" aria-hidden="true">
          {match.heroes.slice(0, 4).map((heroId, slot) => (
            <span key={slot} className="tableRow__face">
              {artOf(heroId) ? <img src={artOf(heroId)} alt="" loading="lazy" draggable={false} /> : <b>?</b>}
            </span>
          ))}
        </span>
        <span className="tableRow__body">
          <span className="tableRow__title">
            {match.heroes.map((h) => (h ? heroName(h) : '?')).join(' vs ')}
          </span>
          <span className="tableRow__meta">
            {mapName(match.map_id)}
            {match.format === 'teams' ? ' · 2v2' : match.format === 'ffa' ? ' · každý za seba' : ''}
            {match.is_public ? ' · verejný' : ''} · {opponents(match, nick)}
          </span>
        </span>
        <span className="tableRow__state">
          <span className={`tableRow__status tableRow__status--${status.tone}`}>{status.label}</span>
          <span className="tableRow__time">
            {match.turn ? `${match.turn}. ťah · ` : ''}
            {ago(match.updated_at)}
          </span>
        </span>
      </button>
      <span className="tableRow__actions">
        {lobby ? (
          <button type="button" className="btn btn--sm btn--primary" onClick={() => void copy()}>
            {copied ? 'Skopírované ✓' : 'Kopírovať pozvánku'}
          </button>
        ) : (
          <button
            type="button"
            className={`btn btn--sm ${status.tone === 'turn' ? 'btn--accent' : 'btn--outlined'}`}
            onClick={onOpen}
          >
            {status.tone === 'turn' ? 'Hrať' : 'Otvoriť'}
          </button>
        )}
      </span>
    </article>
  );
}

/** Verejný stôl cudzieho hráča — klik otvorí predsieň, kde si hráč vyberie postavu. */
function PublicRow({ table, onOpen }: { table: PublicTable; onOpen: () => void }) {
  const taken = table.seats_total - table.seats_free;
  return (
    <article className="tableRow tableRow--lobby">
      <button type="button" className="tableRow__main" onClick={onOpen}>
        <span className="tableRow__duo" aria-hidden="true">
          {table.heroes.slice(0, 4).map((heroId, slot) => (
            <span key={slot} className="tableRow__face">
              {artOf(heroId) ? <img src={artOf(heroId)} alt="" loading="lazy" draggable={false} /> : <b>?</b>}
            </span>
          ))}
        </span>
        <span className="tableRow__body">
          <span className="tableRow__title">
            {table.heroes.map((h) => (h ? heroName(h) : '?')).join(' vs ')}
          </span>
          <span className="tableRow__meta">
            {mapName(table.map_id)}
            {table.format === 'teams' ? ' · 2v2' : table.format === 'ffa' ? ' · každý za seba' : ''} · pri stole:{' '}
            {table.nicks.filter(Boolean).join(', ') || table.host || '—'}
          </span>
        </span>
        <span className="tableRow__state">
          <span className="tableRow__status tableRow__status--lobby">
            Voľné miesta · {taken}/{table.seats_total}
          </span>
          <span className="tableRow__time">{ago(table.created_at)}</span>
        </span>
      </button>
      <span className="tableRow__actions">
        <button type="button" className="btn btn--sm btn--accent" onClick={onOpen}>
          Pridať sa
        </button>
      </span>
    </article>
  );
}

function ResultRow({ match, onOpen }: { match: MatchSummary; onOpen: () => void }) {
  const slots = match.heroes.map((_, slot) => slot);
  const winners = slots.filter((slot) => slotWon(match, slot));
  const losers = slots.filter((slot) => !slotWon(match, slot));
  const label = (slot: number) => {
    const who =
      match.mode === 'online'
        ? match.nicks[slot]
        : match.mode === 'solo'
          ? (match.nicks[slot] ?? (match.bot_levels[slot] ? 'bot' : null))
          : null;
    return who ? `${who} [${heroName(match.heroes[slot])}]` : heroName(match.heroes[slot]);
  };

  return (
    <button type="button" className="result" onClick={onOpen}>
      <span className="result__icon" aria-hidden="true">
        {artOf(match.heroes[winners[0] ?? 0]) ? (
          <img src={artOf(match.heroes[winners[0] ?? 0])} alt="" loading="lazy" draggable={false} />
        ) : (
          '★'
        )}
      </span>
      <span className="result__body">
        <span className="result__line">
          {/* „víťazstvo nad" — bez slovesa, ktoré by hádalo rod hráča. */}
          {winners.map(label).join(' + ')} <em>— víťazstvo nad</em> {losers.map(label).join(', ')}
        </span>
        <span className="result__meta">
          {ago(match.updated_at)} · {mapName(match.map_id)}
          {match.mode === 'online' ? '' : ` · ${MODE_NAME[match.mode]}`}
        </span>
      </span>
    </button>
  );
}
